import { Chip, ClickAwayListener, Grid, InputAdornment } from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { DatePicker, Icons, Select, TextField, Tooltip, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import type { ComponentType } from '../../rxdb/componentType/componentType.types';
import type {
  ComponentAreaInfo,
  DeficiencyList,
} from '../../rxdb/deficiencyList/deficiencyList.types';
import type { ElevationRange } from '../../rxdb/elevationRange/elevationRange.types';
import type { PrimaryCauseOfFailure } from '../../rxdb/primaryCauseOfFailure/primaryCauseOfFailure.types';
import type { RemedialAction } from '../../rxdb/remedialAction/remedialAction.types';
import { ComponentInfo } from '../../utilities/enums';
import { isNull } from '../../utilities/utilityFunctions';
import useStyles from '../Deficiencies/Deficiencies.styles';

interface Props {
  deficiencyPayloadObj: DeficiencyList;
  componentTypeListData: ComponentType[];
  remedialActionListData: RemedialAction[];
  primaryCauseOfFailureListData: PrimaryCauseOfFailure[];
  elevationRange: ElevationRange[];
  isShaft: boolean;
  isEditable: boolean;
  selectComponentLocations: () => void;
  validateInputs: () => void;
  findTrafficSafetyScore: () => void;
  setDeficiencyPayload: React.Dispatch<React.SetStateAction<DeficiencyList>>;
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

const ComponentInfoPanel: React.FC<Props> = ({
  deficiencyPayloadObj,
  componentTypeListData,
  remedialActionListData,
  primaryCauseOfFailureListData,
  isShaft,
  elevationRange,
  isEditable,
  selectComponentLocations,
  validateInputs,
  findTrafficSafetyScore,
  setDeficiencyPayload,
  handleKeyDown,
}) => {
  const classes = useStyles();
  const { CurrentUserSelected } = useMst();
  const deficiencyPayload = deficiencyPayloadObj;
  const [open, setOpen] = useState<boolean>(false);
  const [componentTypeOtherSelected, setComponentTypeOtherSelected] = useState<boolean>(
    deficiencyPayload[ComponentInfo.COMPONENT_TYPE]?.toLowerCase() === 'other',
  );
  const [primaryCauseOfFailureOtherSelected, setPrimaryCauseOfFailureOtherSelected] =
    useState<boolean>(deficiencyPayload[ComponentInfo.CAUSE_OF_FAILURE]?.toLowerCase() === 'other');
  const [remedialActionOtherSelected, setRemedialActionOtherSelected] = useState<boolean>(
    deficiencyPayload[ComponentInfo.REMEDIAL_ACTION]?.toLowerCase() === 'other',
  );
  const [date, setDate] = useState(
    isNull(deficiencyPayload, ComponentInfo.DATE_OBSERVED, new Date()),
  );

  const setDropDownValues = (name: string, value: string) => {
    let data: RemedialAction | PrimaryCauseOfFailure | ComponentType;
    switch (name) {
      case ComponentInfo.REMEDIAL_ACTION:
        data = remedialActionListData.find(item => item.id === value) as RemedialAction;

        if (data) {
          if (data.name?.toLowerCase() === 'other') setRemedialActionOtherSelected(true);
          else setRemedialActionOtherSelected(false);
          setDeficiencyPayload((prev: DeficiencyList) => {
            return {
              ...prev,
              isChanged: true,
              [ComponentInfo.REMEDIAL_ACTION]: data.name,
              [ComponentInfo.REMEDIAL_ACTION_ID]: Number(data.id),
            };
          });
        }
        return;
      case ComponentInfo.CAUSE_OF_FAILURE:
        data = primaryCauseOfFailureListData.find(
          item => item.id === value,
        ) as PrimaryCauseOfFailure;
        if (data) {
          if (data.name?.toLowerCase() === 'other') setPrimaryCauseOfFailureOtherSelected(true);
          else setPrimaryCauseOfFailureOtherSelected(false);
          setDeficiencyPayload((prev: DeficiencyList) => {
            return {
              ...prev,
              isChanged: true,
              [ComponentInfo.CAUSE_OF_FAILURE]: data.name,
              [ComponentInfo.FAILURE_CAUSE_CATEGORY]: Number(data.id),
            };
          });
        }
        return;
      case ComponentInfo.COMPONENT_TYPE:
        data = componentTypeListData.find(
          (item: ComponentType) => item.id === value,
        ) as ComponentType;
        if (data) {
          if (data.name?.toLowerCase() === 'other') setComponentTypeOtherSelected(true);
          else setComponentTypeOtherSelected(false);
          setDeficiencyPayload((prev: DeficiencyList) => {
            return {
              ...prev,
              isChanged: true,
              [ComponentInfo.COMPONENT_TYPE]: data.name,
              [isShaft ? ComponentInfo.FK_COMPONENT : ComponentInfo.ASSET_CATEGORY_COMPONENT_ID]:
                Number(data.id),
            };
          });
        }
        return;
      default:
        return;
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (
      name === ComponentInfo.REMEDIAL_ACTION ||
      name === ComponentInfo.CAUSE_OF_FAILURE ||
      name === ComponentInfo.COMPONENT_TYPE
    ) {
      setDropDownValues(name, value);
    } else {
      setDeficiencyPayload((prev: DeficiencyList) => {
        return {
          ...prev,
          isChanged: true,
          [name]: value,
        };
      });
    }
  };

  const handleDateChange = (event: MaterialUiPickersDate) => {
    if (event) {
      setDeficiencyPayload((prev: DeficiencyList) => {
        return {
          ...prev,
          isChanged: true,
          [ComponentInfo.DATE_OBSERVED]: event.toISOString(),
        };
      });
      setDate(new Date(event.toISOString()));
    }
  };

  const handleDeleteComponentLocation = (id: number) => {
    const data = isNull(deficiencyPayload, ComponentInfo.COMPONENT_AREA_INFO, []).filter(
      (val: ComponentAreaInfo) => val.areaId !== id,
    );
    setDeficiencyPayload((prev: DeficiencyList) => {
      return {
        ...prev,
        isChanged: true,
        [ComponentInfo.COMPONENT_AREA_INFO]: data,
      };
    });
  };

  const chip = (val: ComponentAreaInfo) => {
    return (
      <Chip
        key={val.areaId}
        variant="outlined"
        clickable
        onDelete={() => handleDeleteComponentLocation(val.areaId)}
        disabled={!isEditable}
        label={val.areaPcsId}
        deleteIcon={!isEditable || isShaft ? <></> : <Icons.MinusCircleFeather color="error" />}
      />
    );
  };

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const handleRange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { name, value } = event.target;
    setDeficiencyPayload((prev: DeficiencyList) => {
      return {
        ...prev,
        isChanged: true,
        [name]: value,
      };
    });
  };

  const ComponentAreaView = () => {
    return deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.length > 1 ? (
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <Grid className={classes.displayToolTip}>
          {deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.map(
            (val: ComponentAreaInfo, index: number) => {
              if (index < 1) {
                return chip(val);
              }
            },
          )}
          <Tooltip
            PopperProps={{
              disablePortal: true,
            }}
            onClose={handleTooltipClose}
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={
              <Grid className="tooltipLocation">
                {deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.map(
                  (val: ComponentAreaInfo, index: number) => {
                    if (index > 0) {
                      return chip(val);
                    }
                  },
                )}
              </Grid>
            }
            placement="bottom"
            arrow
            className={classes.tooltip}
          >
            <Typography color="primary" className={classes.seeMoreText} onClick={handleTooltipOpen}>
              {`See More (${deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.length - 1})`}
            </Typography>
          </Tooltip>
        </Grid>
      </ClickAwayListener>
    ) : (
      <Grid>
        {deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.map((val: ComponentAreaInfo) => {
          return chip(val);
        })}
      </Grid>
    );
  };

  useEffect(() => {
    validateInputs();
  }, [deficiencyPayload]);

  useEffect(() => {
    findTrafficSafetyScore();
  }, [deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]]);

  const isBetweenRange = (left: number, right: number, mid: number) => {
    return left <= mid && mid <= right;
  };

  const ElevationFilter = (elevation: ElevationRange) => {
    return (
      elevation.upperElevation?.length &&
      elevation.lowerElevation?.length &&
      deficiencyPayload.lowerElevation?.length &&
      deficiencyPayload.upperElevation?.length &&
      Number(deficiencyPayload.upperElevation) <= Number(deficiencyPayload.lowerElevation) &&
      (isBetweenRange(
        Number(deficiencyPayload.upperElevation),
        Number(deficiencyPayload.lowerElevation),
        Number(elevation.upperElevation),
      ) ||
        isBetweenRange(
          Number(deficiencyPayload.upperElevation),
          Number(deficiencyPayload.lowerElevation),
          Number(elevation.lowerElevation),
        ) ||
        isBetweenRange(
          Number(elevation.upperElevation),
          Number(elevation.lowerElevation),
          Number(deficiencyPayload.upperElevation),
        ) ||
        isBetweenRange(
          Number(elevation.upperElevation),
          Number(elevation.lowerElevation),
          Number(deficiencyPayload.lowerElevation),
        ))
    );
  };

  useEffect(() => {
    if (isShaft) {
      const elevationData = elevationRange
        .filter((elevation: ElevationRange) => ElevationFilter(elevation))
        .map((elevation: ElevationRange) => {
          return {
            areaId: Number(elevation.areaId),
            areaPcsId: elevation.areaPcsId,
            trafficSafety: elevation.trafficSafety,
            inspectionId: CurrentUserSelected.inspectionId,
            componentVersionId: deficiencyPayload.componentVersionId,
          };
        });
      setDeficiencyPayload((prev: DeficiencyList) => {
        return {
          ...prev,
          [ComponentInfo.COMPONENT_AREA_INFO]: JSON.parse(JSON.stringify(elevationData)),
        };
      });
    }
  }, [elevationRange, deficiencyPayload.lowerElevation, deficiencyPayload.upperElevation]);

  return (
    <Grid className={classes.outerGrid} xs={12} item container>
      <Grid container spacing={2}>
        <Grid item container xs={12} alignItems="center">
          <Grid item xs={12} className={classes.compoInfoGrid}>
            <Typography variant="h6">Component Info</Typography>
            <Typography variant="caption">
              <span>
                Fields with<span className={classes.requierd}> *</span> are mandatory
              </span>
            </Typography>
            <hr className={`${classes.breakLine} ${classes.checkboxLabel}`} />
          </Grid>
          <Grid item xs={12} className={classes.componentInfoDisplay}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>
                  Deficiency Details
                  {primaryCauseOfFailureOtherSelected && (
                    <span className={classes.requierd}> *</span>
                  )}
                </span>
              </Typography>
              <TextField
                inputProps={{
                  'data-testid': 'deficiency-details',
                }}
                name={ComponentInfo.COMMENT_OBSERVATION}
                noMinHeight
                size="small"
                placeholder="Type deficiency details"
                onChange={handleChange}
                defaultValue={isNull(deficiencyPayload, ComponentInfo.COMMENT_OBSERVATION, '')}
                disabled={!isEditable}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>Primary Cause of Failure</span>
              </Typography>
              <Select
                inputProps={{
                  'data-testid': 'primary-cause-failure',
                }}
                name={ComponentInfo.CAUSE_OF_FAILURE}
                list={primaryCauseOfFailureListData}
                required
                getOptionLabel={list => `${list?.name}`}
                getOptionValue={list => list?.id}
                onChange={handleChange}
                placeholder="Primary Cause of Failure"
                className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
                defaultValue={isNull(deficiencyPayload, ComponentInfo.FAILURE_CAUSE_CATEGORY, '')}
                disabled={!isEditable}
                noMinHeight
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.componentInfoDisplay}>
          <Grid item xs={6} className={classes.componentGrid}>
            <Typography variant="subtitle2" className="fieldLabel">
              <span>
                Component Name <span className={classes.requierd}> *</span>
              </span>
            </Typography>
            <TextField
              inputProps={{
                'data-testid': 'component-name',
              }}
              noMinHeight
              name={ComponentInfo.NAME}
              size="small"
              placeholder="Type component name"
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              defaultValue={isNull(deficiencyPayload, ComponentInfo.NAME, '')}
              disabled={!isEditable}
              className={classes.componentTextField}
            />
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={6}>
            <Typography variant="subtitle2" className="fieldLabel">
              <span>
                {isShaft ? 'Deficiency Height Range (ft)' : 'Component Location'}
                <span className={classes.requierd}> *</span>
              </span>
            </Typography>
            {isShaft ? (
              <Grid item xs={12} className={classes.shaftComponentLocationGrid}>
                <Grid item>
                  <TextField
                    noMinHeight
                    type="number"
                    name={ComponentInfo.UPPER_ELEVATION}
                    onChange={handleRange}
                    value={deficiencyPayload.upperElevation}
                    variant="empty"
                    disabled={!isEditable}
                    className="rangeSelector"
                    InputProps={{
                      className: 'notchedOutline',
                    }}
                  />
                </Grid>
                <Typography color="primary" className="firstHorizontalLine">
                  -
                </Typography>

                <Grid item>
                  <TextField
                    noMinHeight
                    type="number"
                    name={ComponentInfo.LOWER_ELEVATION}
                    onChange={handleRange}
                    value={deficiencyPayload.lowerElevation}
                    disabled={!isEditable}
                    variant="empty"
                    className="rangeSelector"
                    InputProps={{
                      className: 'notchedOutline',
                    }}
                  />
                </Grid>
                <Typography color="primary" className="secondHorizontalLine">
                  -
                </Typography>
                <ComponentAreaView />
              </Grid>
            ) : (
              <Grid item className={classes.componentLocationGrid}>
                <ComponentAreaView />
                {isEditable && (
                  <Icons.PlusCircleFeather
                    data-testid="clPlusButton"
                    color="primary"
                    className={classes.componentLocationPlusButton}
                    onClick={selectComponentLocations}
                  />
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.componentInfoDisplay}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" className="fieldLabel">
              <span>Location Comment</span>
            </Typography>
            <TextField
              inputProps={{
                'data-testid': 'location-comment',
              }}
              noMinHeight
              name={ComponentInfo.LOCATION_DESCRIPTION}
              size="small"
              placeholder="Select component location"
              onChange={handleChange}
              defaultValue={isNull(deficiencyPayload, ComponentInfo.LOCATION_DESCRIPTION, '')}
              disabled={!isEditable}
              onKeyDown={handleKeyDown}
            />
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={6}>
            <Typography variant="subtitle2" className="fieldLabel">
              <span>Date Observed</span>
            </Typography>
            <DatePicker
              inputProps={{
                'data-testid': 'date-observed',
              }}
              name={ComponentInfo.DATE_OBSERVED}
              color="primary"
              format="YYYY/MM/DD"
              inputVariant="outlined"
              maxDate={new Date()}
              clearLabel={false}
              autoOk
              onChange={handleDateChange}
              placeholder="Select Date"
              endAdornment={
                <InputAdornment position="end">
                  <Icons.CalendarFeather color="primary" />
                </InputAdornment>
              }
              value={date}
              variant="dialog"
              noMinHeight
              className={classes.selectHeight}
              disabled={!isEditable}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.componentInfoDisplay}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" className="fieldLabel">
              <span>
                Component Type<span className={classes.requierd}> *</span>
              </span>
            </Typography>
            <Select
              inputProps={{
                'data-testid': 'component-type-input',
              }}
              name={ComponentInfo.COMPONENT_TYPE}
              list={componentTypeListData}
              required
              getOptionLabel={(list: { name: string }) => `${list?.name}`}
              getOptionValue={list => list?.id}
              onChange={handleChange}
              placeholder="Select a type"
              defaultValue={
                isShaft
                  ? isNull(deficiencyPayload, ComponentInfo.FK_COMPONENT, '')
                  : isNull(deficiencyPayload, ComponentInfo.ASSET_CATEGORY_COMPONENT_ID, '')
              }
              disabled={!isEditable}
              className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
              noMinHeight
            />
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={6}>
            <Typography variant="subtitle2" className="fieldLabel">
              <span>
                Component Type Details
                {componentTypeOtherSelected && <span className={classes.requierd}> *</span>}
              </span>
            </Typography>
            <TextField
              inputProps={{
                'data-testid': 'component-type-details',
              }}
              name={ComponentInfo.COMMENT_ASSET_COMPONENT}
              noMinHeight
              size="small"
              placeholder="Type component details"
              onChange={handleChange}
              defaultValue={isNull(deficiencyPayload, ComponentInfo.COMMENT_ASSET_COMPONENT, '')}
              disabled={!isEditable}
              onKeyDown={handleKeyDown}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.componentInfoDisplay}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" className="fieldLabel">
              <span>
                Remedial action
                <span className={classes.requierd}> *</span>
              </span>
            </Typography>
            <Select
              inputProps={{
                'data-testid': 'remedial-action',
              }}
              name={ComponentInfo.REMEDIAL_ACTION}
              list={remedialActionListData}
              required
              getOptionLabel={list => `${list?.name}`}
              getOptionValue={list => list?.id}
              onChange={handleChange}
              placeholder="Select a action"
              defaultValue={isNull(deficiencyPayload, ComponentInfo.REMEDIAL_ACTION_ID, '')}
              className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
              noMinHeight
              disabled={!isEditable}
            />
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={6}>
            <Typography variant="subtitle2" className="fieldLabel">
              <span>
                Remedial action details
                {remedialActionOtherSelected && <span className={classes.requierd}> *</span>}
              </span>
            </Typography>
            <TextField
              inputProps={{
                'data-testid': 'remedial-action-details',
              }}
              name={ComponentInfo.COMMENT_REMEDIAL_ACTION}
              noMinHeight
              size="small"
              placeholder="Type remedial action details"
              onChange={handleChange}
              defaultValue={isNull(deficiencyPayload, ComponentInfo.COMMENT_REMEDIAL_ACTION, '')}
              disabled={!isEditable}
              onKeyDown={handleKeyDown}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ComponentInfoPanel;
